import React from 'react'

import Link from 'next/link'
import { HiExclamationTriangle, HiOutlineCog6Tooth } from 'react-icons/hi2'

import IconButton from '@/components/common/IconButtonV2'
import { Statistic } from '@/components/home/ui/Statistic'
import { PipelineStageSelectionPopover } from '@/components/pursuits/shared/PipelineStageSelectionPopover'
import { EMPTY_ESTIMATED_VALUE } from '@/components/pursuits/shared/renderPursuitField'

import {
  formatDollarAmount,
  formatDollarAmountWithSuffix,
} from '@/helpers/string'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { usePipelineEstimatedValue } from '@/hooks/pursuits/summary/usePipelineEstimatedValue'

export const EstimatedValueStatistics: React.FC = () => {
  const { activeOrgId } = useActiveOrgId()

  const {
    totalValue,
    nullOrZeroCount,
    isLoading,
    selectedStageIds,
    setSelectedStageIds,
    stageStatistics,
    stages,
  } = usePipelineEstimatedValue()

  if (isLoading || !stages || stages.length === 0) {
    return <Statistic.Skeleton />
  }

  return (
    <Statistic
      label="Pipeline Est. Value ($)"
      value={
        totalValue && totalValue > 0
          ? totalValue >= 1000
            ? formatDollarAmountWithSuffix(totalValue, 1)
            : formatDollarAmount(totalValue)
          : EMPTY_ESTIMATED_VALUE
      }
    >
      <div className="flex items-center justify-between gap-2 w-full h-5">
        <div className="-ml-1">
          {nullOrZeroCount > 0 && (
            <IconButton
              className="flex items-center gap-1.5 text-yellow-500 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/30"
              tooltip={
                <div className="flex flex-col gap-1">
                  <p>
                    {nullOrZeroCount.toLocaleString()} pursuit
                    {nullOrZeroCount === 1 ? '' : 's'} are missing estimated
                    value{nullOrZeroCount === 1 ? '' : 's'}
                  </p>
                  <Link
                    href={`/orgs/${activeOrgId}/pursuits`}
                    className="text-left text-sm text-blue-600 dark:text-blue-400 hover:underline underline-offset-2"
                  >
                    View your pursuits
                  </Link>
                </div>
              }
              tooltipProps={{ delayDuration: 0 }}
              tooltipContentProps={{
                className: 'max-w-[300px] py-2',
                inverseColors: true,
                side: 'bottom',
              }}
            >
              <Link
                href={`/orgs/${activeOrgId}/pursuits`}
                className="flex items-center gap-1.5"
              >
                <HiExclamationTriangle size={18} />
                <span className="text-xs font-medium text-yellow-600 dark:text-yellow-500">
                  Missing pursuit est. values
                </span>
              </Link>
            </IconButton>
          )}
        </div>
        <div className="flex items-center gap-2">
          <PipelineStageSelectionPopover
            selectedStageIds={selectedStageIds}
            setSelectedStageIds={setSelectedStageIds}
            stageStatistics={stageStatistics}
            stages={stages}
          >
            <IconButton
              tooltip="Configure stages"
              tooltipProps={{ delayDuration: 0 }}
              className="flex items-center gap-1.5"
            >
              <HiOutlineCog6Tooth size={18} />
            </IconButton>
          </PipelineStageSelectionPopover>
        </div>
      </div>
    </Statistic>
  )
}

EstimatedValueStatistics.displayName = 'Home.Dashboard.EstimatedValueStatistics'
